import { Injectable } from "@angular/core";
import { DynamicsClientService } from "@omni/data-services/dynamics-client/dynamics-client.service";
import { DeviceService } from "../device/device.service";
import { AuthenticationService } from "../authentication.service";
import { DB_KEY_PREFIXES } from "@omni/config/pouch-db.config";
import { DiskService } from "../disk/disk.service";
import { fetchQueries } from "@omni/config/dynamics-fetchQueries";
import { LadderHistoryEntry } from "@omni/classes/customer-segment/adoption-ladder.class";
import { MarketScanDataService } from "@omni/data-services/market-scan/market-scan.data.service";
import { BrandOfflineService } from "../brand/brand.service";
import { ProductRatingOfflineService } from "../product-rating/product-rating-offline.service";
import { isEmpty } from 'lodash-es';

@Injectable({
  providedIn: 'root',
})

export class AdoptionLadderJourneyService {

  public adoptionLadderData: any[] = [];
  public contactJourneySuggestionData: any[] = [];
  public contactRxSalesData: any[] = [];
  public contactCustomerSegmentationData: any[] = [];
  public accountCustomerSegmentationData: any[] = [];
  public accountJourneySuggestionData: any[] = [];
  public accountRxSalesData: any[] = [];

  constructor(
    private dynamics: DynamicsClientService,
    private device: DeviceService,
    private authenticationService: AuthenticationService,
    private disk: DiskService,
    private readonly marketScanDataService: MarketScanDataService,
    private readonly brandOfflineService: BrandOfflineService,
    private readonly productRatingOfflineService: ProductRatingOfflineService,
  ) {}

  /** Product eligibility (producttype 100000002, active, assigned to this position) — same check
   * MarketScanDataService.filterAndMapRows already applies to the bulk segmentation/ladder
   * fetches. This service's live per-contact/account fetches are a separate code path that never
   * went through that filter, so a product not assigned to the user's position could still show
   * up in the Journey tab. Filtered client-side against BrandOfflineService.brands, same as the
   * bulk fetches, rather than joining product/indskr_productassignment/position in the fetch. */
  private filterToEligibleProducts<T extends { productId: string }>(rows: T[]): T[] {
    const eligibleProductIds = new Set(
      (this.brandOfflineService.brands ?? [])
        .filter(brand => brand.producttype === 100000002)
        .map(brand => brand.ID)
    );
    return (rows ?? []).filter(row => eligibleProductIds.has(row.productId));
  }

  public async getAdoptionLadderData(): Promise<any[]> {
    if (!isEmpty(this.adoptionLadderData)) {
      return this.adoptionLadderData;
    }

    try {
      const doc = await this.disk.retrieve(DB_KEY_PREFIXES.ADOPTION_LADDER_DATA, true);
      if (doc && doc.raw) {
        this.adoptionLadderData = doc.raw;
        return this.adoptionLadderData;
      }
    } catch (error) {
      console.error('Error retrieving adoption ladder data:', error);
      return [];
    }
    return [];
  }

  public getAdoptionLadderDataMap(): Map<string, { name: string; rank: any; ladderTypeId: string; ladderTypeName: string }> {
    const adoptionLadderMap = new Map<string, { name: string; rank: any; ladderTypeId: string; ladderTypeName: string }>();
    if (this.adoptionLadderData && Array.isArray(this.adoptionLadderData)) {
      this.adoptionLadderData.forEach(level => {
        adoptionLadderMap.set(level.indskr_adoptionladderid, {
          name: level.indskr_name,
          rank: level.indskr_rank,
          ladderTypeId: level.ladderTypeId || 'default',
          ladderTypeName: level.ladderTypeName || 'Adoption Ladder',
        });
      });
    }
    return adoptionLadderMap;
  }

  public getAdoptionLadderDataByType(): Map<string, { typeName: string; stages: { id: string; name: string; rank: any }[] }> {
    const typeMap = new Map<string, { typeName: string; stages: { id: string; name: string; rank: any }[] }>();
    if (this.adoptionLadderData && Array.isArray(this.adoptionLadderData)) {
      this.adoptionLadderData.forEach(level => {
        const typeId: string = level.ladderTypeId || 'default';
        const typeName: string = level.ladderTypeName || 'Adoption Ladder';
        if (!typeMap.has(typeId)) {
          typeMap.set(typeId, { typeName, stages: [] });
        }
        typeMap.get(typeId).stages.push({
          id: level.indskr_adoptionladderid,
          name: level.indskr_name,
          rank: level.indskr_rank,
        });
      });
    }
    return typeMap;
  }

  public async fetchAdoptionLadderData(loadFromDbOnly = false): Promise<void> {
    if (loadFromDbOnly) {
      return;
    }
    const ladderTypeIdValues = await this.marketScanDataService.buildLadderTypeIdValues();
    const fetchXml = fetchQueries.fetchAdoptionLadderData.split('{ladderTypeIds}').join(ladderTypeIdValues);

    try {
      const response = await this.dynamics.executeFetchQuery('indskr_adoptionladders', fetchXml);

      if (Array.isArray(response) && response.length > 0) {
        try {
          await this.disk.updateOrInsert(DB_KEY_PREFIXES.ADOPTION_LADDER_DATA, () => ({ raw: response }));
          this.adoptionLadderData = response;
        } catch (diskError) {
          console.error('Failed to update adoption ladder data in disk:', diskError);
        }
      }
    } catch (error) {
      this.adoptionLadderData = [];
      console.error('Error executing fetch query for adoption ladder data:', error);
    }
  }

  /** Builds the XML value list for FetchXML IN filters: <value>roleId</value>... */
  public getUserRoleIdValues(): string {
    const roles = this.authenticationService.user.securityRoles || [];
    const values = roles
      .map((r: any) => r.roleid || r.roleId || r.RoleId || '')
      .filter(id => !!id)
      .map(id => `<value>${id}</value>`)
      .join('');
    // Return a non-matching dummy GUID when roles are empty so the FetchXML IN filter stays valid
    return values || '<value>00000000-0000-0000-0000-000000000000</value>';
  }

  /** Always true — ladders are visible by default; FetchXML controls the returned set. */
  public canViewLadder(_viewRoleId: string | null): boolean {
    return true;
  }

  /** Editable only when the edit-role mapping join returned a non-null row. */
  public canEditLadder(editRoleId: string | null): boolean {
    return !!editRoleId;
  }

  public async fetchContactCustomerSegmentationData(contactId: string): Promise<any[]> {
    if (this.device.isOffline || !contactId) return [];

    let fetchXml = fetchQueries.fetchContactCustomerSegmentationData;

    const positionIds = this.authenticationService.user.positions.map(o => o.ID);
    let positionIDValues = '';
    positionIds.forEach(p => { positionIDValues += '<value>' + p + '</value>'; });

    const userRoleIdValues = this.getUserRoleIdValues();
    const ladderTypeIdValues = await this.marketScanDataService.buildLadderTypeIdValues();

    fetchXml = fetchXml.split('{positionIds}').join(positionIDValues);
    fetchXml = fetchXml.split('{parentPositionIds}').join(positionIDValues);
    fetchXml = fetchXml.split('{contactId}').join(contactId);
    fetchXml = fetchXml.split('{userRoleIds}').join(userRoleIdValues);
    fetchXml = fetchXml.split('{ladderTypeIds}').join(ladderTypeIdValues);

    try {
      const response = await this.dynamics.executeFetchQuery('indskr_productratings', fetchXml);
      if (response && response.length > 0) {
        this.contactCustomerSegmentationData = this.filterToEligibleProducts(response);
        return this.contactCustomerSegmentationData;
      }
      return [];
    } catch (error) {
      this.contactCustomerSegmentationData = [];
      console.error('Error fetching contact customer segmentations data:', error);
      return [];
    }
  }

  public async fetchContactJourneySuggestionData(contactId: string): Promise<any[]> {
    if (!contactId || this.device.isOffline) return [];

    let fetchXml = fetchQueries.fetchContactJourneySuggestionData;

    const positionIds = this.authenticationService.user.positions.map(o => o.ID);
    let positionIDValues = '';
    positionIds.forEach(p => { positionIDValues += '<value>' + p + '</value>'; });

    fetchXml = fetchXml.split('{positionIds}').join(positionIDValues);
    fetchXml = fetchXml.split('{parentPositionIds}').join(positionIDValues);
    fetchXml = fetchXml.split('{contactId}').join(contactId);

    try {
      const response = await this.dynamics.executeFetchQuery('indskr_growthsuggestions', fetchXml);
      if (response && response.length > 0) {
        this.contactJourneySuggestionData = response;
        return this.contactJourneySuggestionData;
      }
      return [];
    } catch (error) {
      this.contactJourneySuggestionData = [];
      console.error('Error fetching contact journey suggestion data:', error);
      return [];
    }
  }

  public async fetchContactRxSalesData(contactId: string): Promise<any[]> {
    if (!contactId || this.device.isOffline) return [];

    let fetchXml = fetchQueries.fetchContactRxSalesData;

    const positionIds = this.authenticationService.user.positions.map(o => o.ID);
    let positionIDValues = '';
    positionIds.forEach(p => { positionIDValues += '<value>' + p + '</value>'; });

    fetchXml = fetchXml.split('{positionIds}').join(positionIDValues);
    fetchXml = fetchXml.split('{parentPositionIds}').join(positionIDValues);
    fetchXml = fetchXml.split('{contactId}').join(contactId);

    try {
      const response = await this.dynamics.executeFetchQuery('indskr_rxsalesdatas', fetchXml);
      if (response && response.length > 0) {
        this.contactRxSalesData = response;
        return this.contactRxSalesData;
      }
      return [];
    } catch (error) {
      this.contactRxSalesData = [];
      console.error('Error fetching contact rx sales data:', error);
      return [];
    }
  }

  public async getContactJourneySuggestionData(contactId: string): Promise<any[]> {
    if (!isEmpty(this.contactJourneySuggestionData) && this.contactJourneySuggestionData[0]['contact.contactid'] === contactId) {
      return this.contactJourneySuggestionData;
    }
    this.contactJourneySuggestionData = [];
    const data = await this.fetchContactJourneySuggestionData(contactId);
    this.contactJourneySuggestionData = data;
    return this.contactJourneySuggestionData;
  }

  public async getContactCustomerSegmentationData(contactId: string): Promise<any[]> {
    if (!isEmpty(this.contactCustomerSegmentationData) && this.contactCustomerSegmentationData[0]['contact.contactid'] === contactId) {
      return this.contactCustomerSegmentationData;
    }
    this.contactCustomerSegmentationData = [];
    const data = await this.fetchContactCustomerSegmentationData(contactId);
    this.contactCustomerSegmentationData = data;
    return this.contactCustomerSegmentationData;
  }

  public async getContactRxSalesData(contactId: string): Promise<any[]> {
    if (!isEmpty(this.contactRxSalesData) && this.contactRxSalesData[0]['contact.contactid'] === contactId) {
      return this.contactRxSalesData;
    }
    this.contactRxSalesData = [];
    const data = await this.fetchContactRxSalesData(contactId);
    this.contactRxSalesData = data;
    return this.contactRxSalesData;
  }

  public async fetchAccountCustomerSegmentationData(accountId: string): Promise<any[]> {
    if (this.device.isOffline || !accountId) return [];

    let fetchXml = fetchQueries.fetchAccountCustomerSegmentationData;

    const positionIds = this.authenticationService.user.positions.map(o => o.ID);
    let positionIDValues = '';
    positionIds.forEach(p => { positionIDValues += '<value>' + p + '</value>'; });

    const userRoleIdValues = this.getUserRoleIdValues();
    const ladderTypeIdValues = await this.marketScanDataService.buildLadderTypeIdValues();

    fetchXml = fetchXml.split('{positionIds}').join(positionIDValues);
    fetchXml = fetchXml.split('{parentPositionIds}').join(positionIDValues);
    fetchXml = fetchXml.split('{accountId}').join(accountId);
    fetchXml = fetchXml.split('{userRoleIds}').join(userRoleIdValues);
    fetchXml = fetchXml.split('{ladderTypeIds}').join(ladderTypeIdValues);

    try {
      const response = await this.dynamics.executeFetchQuery('indskr_productratings', fetchXml);
      if (response && response.length > 0) {
        this.accountCustomerSegmentationData = this.filterToEligibleProducts(response);
        return this.accountCustomerSegmentationData;
      }
      return [];
    } catch (error) {
      this.accountCustomerSegmentationData = [];
      console.error('Error fetching account customer segmentations data:', error);
      return [];
    }
  }

  public async fetchAccountJourneySuggestionData(accountId: string): Promise<any[]> {
    if (!accountId || this.device.isOffline) return [];

    let fetchXml = fetchQueries.fetchAccountJourneySuggestionData;

    const positionIds = this.authenticationService.user.positions.map(o => o.ID);
    let positionIDValues = '';
    positionIds.forEach(p => { positionIDValues += '<value>' + p + '</value>'; });

    fetchXml = fetchXml.split('{positionIds}').join(positionIDValues);
    fetchXml = fetchXml.split('{parentPositionIds}').join(positionIDValues);
    fetchXml = fetchXml.split('{accountId}').join(accountId);

    try {
      const response = await this.dynamics.executeFetchQuery('indskr_growthsuggestions', fetchXml);
      if (response && response.length > 0) {
        this.accountJourneySuggestionData = response;
        return this.accountJourneySuggestionData;
      }
      return [];
    } catch (error) {
      this.accountJourneySuggestionData = [];
      console.error('Error fetching account journey suggestion data:', error);
      return [];
    }
  }

  public async fetchAccountRxSalesData(accountId: string): Promise<any[]> {
    if (!accountId || this.device.isOffline) return [];

    let fetchXml = fetchQueries.fetchAccountRxSalesData;

    const positionIds = this.authenticationService.user.positions.map(o => o.ID);
    let positionIDValues = '';
    positionIds.forEach(p => { positionIDValues += '<value>' + p + '</value>'; });

    fetchXml = fetchXml.split('{positionIds}').join(positionIDValues);
    fetchXml = fetchXml.split('{parentPositionIds}').join(positionIDValues);
    fetchXml = fetchXml.split('{accountId}').join(accountId);

    try {
      const response = await this.dynamics.executeFetchQuery('indskr_rxsalesdatas', fetchXml);
      if (response && response.length > 0) {
        this.accountRxSalesData = response;
        return this.accountRxSalesData;
      }
      return [];
    } catch (error) {
      this.accountRxSalesData = [];
      console.error('Error fetching account rx sales data:', error);
      return [];
    }
  }

  public async getAccountJourneySuggestionData(accountId: string): Promise<any[]> {
    if (!isEmpty(this.accountJourneySuggestionData) && this.accountJourneySuggestionData[0]['account.accountid'] === accountId) {
      return this.accountJourneySuggestionData;
    }
    this.accountJourneySuggestionData = [];
    const data = await this.fetchAccountJourneySuggestionData(accountId);
    this.accountJourneySuggestionData = data;
    return this.accountJourneySuggestionData;
  }

  public async getAccountCustomerSegmentationData(accountId: string): Promise<any[]> {
    if (!isEmpty(this.accountCustomerSegmentationData) && this.accountCustomerSegmentationData[0]['account.accountid'] === accountId) {
      return this.accountCustomerSegmentationData;
    }
    this.accountCustomerSegmentationData = [];
    const data = await this.fetchAccountCustomerSegmentationData(accountId);
    this.accountCustomerSegmentationData = data;
    return this.accountCustomerSegmentationData;
  }

  public async getAccountRxSalesData(accountId: string): Promise<any[]> {
    if (!isEmpty(this.accountRxSalesData) && this.accountRxSalesData[0]['account.accountid'] === accountId) {
      return this.accountRxSalesData;
    }
    this.accountRxSalesData = [];
    const data = await this.fetchAccountRxSalesData(accountId);
    this.accountRxSalesData = data;
    return this.accountRxSalesData;
  }

  public async fetchLadderStageHistory(productRatingId: string): Promise<LadderHistoryEntry[]> {
    if (this.device.isOffline || !productRatingId) return [];

    let fetchXml = fetchQueries.fetchLadderStageHistory;
    fetchXml = fetchXml.replace('{productRatingId}', productRatingId);

    try {
      const response = await this.dynamics.executeFetchQuery('indskr_transactionlogs', fetchXml);
      if (!Array.isArray(response)) return [];
      return response.map((entry: any): LadderHistoryEntry => {
        const newRank = Number(entry['newStageRank']) || 0;
        const prevRank = Number(entry['previousStageRank']) || 0;
        const prevId: string | null = entry['previousStageId'] || null;
        let direction: 'up' | 'down' | 'same' | null = null;
        if (prevId) {
          direction = newRank > prevRank ? 'up' : newRank < prevRank ? 'down' : 'same';
        }
        return {
          transactionLogId: entry['indskr_transactionlogid'] || '',
          previousStageId: prevId,
          previousStageName: entry['previousStageName'] || '',
          previousStageRank: prevRank,
          newStageId: entry['newStageId'] || '',
          newStageName: entry['newStageName'] || '',
          newStageRank: newRank,
          changedOn: entry['createdon'] || '',
          changedByUserName: entry['changedByUserName'] || '',
          direction,
        };
      });
    } catch (error) {
      console.error('Error fetching ladder stage history:', error);
      return [];
    }
  }

  public async updateLadderStage(productRatingId: string, adoptionLadderId: string): Promise<void> {
    if (!productRatingId || !adoptionLadderId) return;
    try {
      await this.dynamics.update(productRatingId, 'indskr_productratings', {
        'indskr_AdoptionLadder@odata.bind': `/indskr_adoptionladders(${adoptionLadderId})`,
      });
      // Only after the remote write lands: keep the cached ladder rows in step so Spotlight's
      // per-stage counts move now rather than at the next delta sync.
      await this.productRatingOfflineService.applyLocalLadderStageChange(productRatingId, adoptionLadderId);
    } catch (error) {
      console.error('Error updating ladder stage:', error);
      throw error;
    }
  }

  public async clearLadderStage(productRatingId: string): Promise<void> {
    if (!productRatingId) return;
    try {
      await this.dynamics.update(productRatingId, 'indskr_productratings', {
        'indskr_adoptionladder@odata.bind': null,
      });
      await this.productRatingOfflineService.applyLocalLadderStageChange(productRatingId, null);
    } catch (error) {
      console.error('Error clearing ladder stage:', error);
      throw error;
    }
  }

}
